import { Pagination } from 'antd';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import { closeModalSelector, getDealersModal, selectDealer } from 'modules/dealers/actions';

import {
  getPage,
  getParams,
  getDealerIds,
  isLoading as isLoadingSelector,
} from 'modules/dealers/selectors/modalSelect';
import { getDealersById } from 'selectors/dealerSelectors';
import { resolveSort } from 'services/SortingUtil';

import Table, { getRowClassName } from 'ui/base/Table';

import Group from './Group';
import useTableScrollY from '../useTableScrollY';

/** @type {Array<import('antd/lib/table').ColumnProps<import('modules/dealers/types').Dealer>>} */
const columns = [
  {
    width: '40%',
    sorter: true,
    fixed: 'left',
    dataIndex: ['name'],
    title: <FormattedMessage id="name" defaultMessage="Name" />,
    render: (_, { id, name }) => <div data-cy={`dealer-${id}`}>{name}</div>,
  },
  {
    minWidth: 100,
    title: <FormattedMessage id="address" defaultMessage="Address" />,
    render: (_, record) => {
      const contact = record.general?.contacts?.find(({ brandId }) => !brandId);
      const fallback = record.general?.contacts?.find(c => c?.address) ?? {};
      return contact?.address ?? fallback?.address;
    },
    className: 'hide-mobile',
  },
  {
    minWidth: 100,
    title: <FormattedMessage id="page.config.dealer_general.city" defaultMessage="City" />,
    render: (_, record) => {
      const contact = record.general?.contacts?.find(({ brandId }) => !brandId);
      const fallback = record.general?.contacts?.find(c => c?.city) ?? {};
      return contact?.city ?? fallback?.city;
    },
    className: 'hide-mobile',
  },
  {
    minWidth: 100,
    title: <FormattedMessage id="country_label" defaultMessage="Country" />,
    dataIndex: ['countryCode'],
    sorter: true,
    render: (_, record) => (record.countryCode ? <FormattedMessage id={`country.${record.countryCode}`} defaultMessage={record.countryCode} /> : null),
    className: 'hide-mobile',
  },
  {
    minWidth: 100,
    title: <FormattedMessage id="group" defaultMessage="Group" />,
    dataIndex: ['groupId'],
    render: id => <Group id={id} />,
    className: 'hide-mobile',
  },
];

/**
 * @typedef {{
 *  meta ?: Object<string, any>
 * }} Props
 *
 * @param {Props} props
 * @returns {React.FC<Props>}
 */
const DealerTable = ({ meta }) => {
  const dispatch = useDispatch();

  const ref = React.useRef(undefined);

  const page = useSelector(getPage);
  const params = useSelector(getParams);
  const dealerIds = useSelector(getDealerIds);
  const isLoading = useSelector(isLoadingSelector);
  const dealers = useSelector(state => getDealersById(state, dealerIds));

  const scrollY = useTableScrollY({ ref });

  const onPageChange = React.useCallback(
    (number, size) => {
      dispatch(
        getDealersModal({
          ...params,
          size,
          page: page.size !== size ? 0 : number - 1,
        })
      );
    },
    [dispatch, params, page]
  );

  const handleOnRow = React.useCallback(
    ({ id }) => ({
      onClick: () => {
        dispatch(closeModalSelector());
        dispatch(selectDealer(id, undefined, undefined, meta));
      },
    }),
    [dispatch, meta]
  );

  const handleOnChange = React.useCallback(
    (_, __, sorter) => {
      const field = Array.isArray(sorter.field) ? sorter.field.join('.') : sorter.field;
      const direction = resolveSort(sorter.order);
      const sort = `${field},${direction}`;
      dispatch(getDealersModal({ ...params, sort }));
    },
    [dispatch, params]
  );

  const getRowKey = React.useCallback(({ id }) => `dealer-${id}`, []);

  return (
    <div className="mt-4 h-100 d-flex overflow-hidden flex-column justify-content-between px-4">
      <div className="h-100 overflow-hidden" ref={ref}>
        <Table
          tableLayout="auto"
          columns={columns}
          rowKey={getRowKey}
          pagination={false}
          onRow={handleOnRow}
          loading={isLoading}
          dataSource={dealers}
          onChange={handleOnChange}
          rowClassName={getRowClassName}
          scroll={{ y: scrollY, x: true }}
        />
      </div>
      <Pagination
        size="small"
        showSizeChanger
        pageSize={page.size}
        onChange={onPageChange}
        current={page.number + 1}
        total={page.totalElements}
      />
    </div>
  );
};

export default DealerTable;
